import { jsPDF } from "jspdf";
import { getCaseReportPdf } from "../api/patientCase";
import { getGenderLabel } from "./emrDisplay";

const PAGE_MARGIN = 12;
const PAGE_TOP = 6;
const BODY_FONT = 9;
const SIGNOFF_HEIGHT = 28;
const SIGNOFF_FOOTER_GAP = 10;
const FOOTER_TEXT_INSET = 5;
const BORDER = [226, 232, 240];
const HEADER_BG = [248, 250, 252];
const INK = [15, 23, 42];
const LABEL = [100, 116, 139];
const HINT = [148, 163, 184];
const LINE = [70, 70, 70];
const WATERMARK_OPACITY = 0.08;

function clean(value) {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text || text === "null" || text === "undefined") return "";
  return text;
}

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${date.getFullYear()}`;
}

function formatTime(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function imageFormat(dataUrl) {
  const head = String(dataUrl || "").slice(0, 48).toLowerCase();
  if (head.includes("image/png")) return "PNG";
  if (head.includes("image/webp")) return "WEBP";
  return "JPEG";
}

function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("image"));
    image.src = src;
  });
}

function withImageMime(dataUrl) {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return dataUrl;
  let header = "";
  try {
    header = atob(dataUrl.slice(comma + 1, comma + 24));
  } catch {
    return dataUrl;
  }
  const bytes = dataUrl.slice(comma + 1);
  if (header.charCodeAt(0) === 0x89 && header.slice(1, 4) === "PNG") return `data:image/png;base64,${bytes}`;
  if (header.charCodeAt(0) === 0xff && header.charCodeAt(1) === 0xd8) return `data:image/jpeg;base64,${bytes}`;
  return dataUrl;
}

/** Keep PNG transparency. Convert formats jsPDF cannot embed into PNG. */
export async function normalizeReportImage(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  const typed = withImageMime(dataUrl);
  const head = typed.slice(0, 64).toLowerCase();
  if (head.startsWith("data:image/png") || head.startsWith("data:image/jpeg") || head.startsWith("data:image/jpg")) {
    return typed;
  }
  if (typeof document === "undefined") return null;
  try {
    const image = await loadHtmlImage(dataUrl);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!width || !height) return null;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0);
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

function imageSize(doc, dataUrl, maxW, maxH) {
  if (!dataUrl) return null;
  try {
    const props = doc.getImageProperties(dataUrl);
    const ratio = props.width / Math.max(props.height, 1);
    let w = maxW;
    let h = w / ratio;
    if (h > maxH) {
      h = maxH;
      w = h * ratio;
    }
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
    return { w, h };
  } catch {
    return null;
  }
}

function paintImage(doc, dataUrl, x, y, w, h) {
  if (!dataUrl || w <= 0 || h <= 0) return false;
  try {
    doc.addImage(dataUrl, imageFormat(dataUrl), x, y, w, h);
    return true;
  } catch {
    return false;
  }
}

/** Same placement rules as the clinic logo: keep the ratio, never invent a broken box. */
function drawReportImage(doc, dataUrl, x, y, maxW, maxH) {
  const size = imageSize(doc, dataUrl, maxW, maxH);
  if (!size) return null;
  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: 1 }));
  const painted = paintImage(doc, dataUrl, x, y, size.w, size.h);
  doc.restoreGraphicsState();
  return painted ? size : null;
}

function doctorLine(name) {
  const text = clean(name);
  if (!text) return "";
  if (/^dr\b/i.test(text)) return text;
  return `Dr. ${text}`;
}

function getReportData(caseData, clinicHeader = {}, doctorInfo = {}) {
  const patient = caseData?.patient || caseData?.Patient || {};
  const vitals = caseData?.latestVitals ?? caseData?.LatestVitals ?? {};
  const report = caseData?.medicalReport ?? caseData?.MedicalReport;
  const firstName = clean(patient?.firstName ?? patient?.FirstName ?? caseData?.patientFirstName ?? caseData?.PatientFirstName);
  const lastName = clean(patient?.lastName ?? patient?.LastName ?? caseData?.patientLastName ?? caseData?.PatientLastName);
  const genderRaw = clean(patient?.gender ?? patient?.Gender ?? caseData?.patientGender ?? caseData?.PatientGender);
  const visitAt = caseData?.createdAt ?? caseData?.CreatedAt ?? "";
  const weight = vitals?.weightKg ?? vitals?.WeightKg ?? vitals?.weight_kg;
  const systolic = vitals?.systolicPressure ?? vitals?.SystolicPressure ?? vitals?.systolic_pressure;
  const diastolic = vitals?.diastolicPressure ?? vitals?.DiastolicPressure ?? vitals?.diastolic_pressure;
  const temperature = vitals?.temperatureC ?? vitals?.TemperatureC ?? vitals?.temperature_c;
  const heartRate = vitals?.heartRate ?? vitals?.HeartRate ?? vitals?.heart_rate;

  const vitalRows = [];
  if (weight != null && weight !== "") {
    const formatted = Number.isFinite(Number(weight)) ? Number(weight).toFixed(1) : String(weight);
    vitalRows.push(["Pesha", `${formatted} kg`]);
  }
  if (systolic != null || diastolic != null) {
    const sys = systolic != null && systolic !== "" ? String(systolic) : "—";
    const dia = diastolic != null && diastolic !== "" ? String(diastolic) : "—";
    vitalRows.push(["Tensioni", `${sys} / ${dia} mmHg`]);
  }
  if (temperature != null && temperature !== "") {
    const formatted = Number.isFinite(Number(temperature)) ? Number(temperature).toFixed(1) : String(temperature);
    vitalRows.push(["Temperatura", `${formatted} °C`]);
  }
  if (heartRate != null && heartRate !== "") vitalRows.push(["Rrahjet e zemrës", `${heartRate} bpm`]);

  const labFileNames = (Array.isArray(caseData?.labFileNames) ? caseData.labFileNames : [])
    .map((name) => clean(name))
    .filter(Boolean);
  const protocol =
    clinicHeader?.useProtocolNumber === true
      ? clean(caseData?.protocolNumber ?? caseData?.ProtocolNumber)
      : "";

  return {
    clinic: {
      name: clean(clinicHeader?.name),
      address: clean(clinicHeader?.address),
      phone: clean(clinicHeader?.phone),
      email: clean(clinicHeader?.email),
      logoBase64: clinicHeader?.logoBase64 || null,
      showWatermark: clinicHeader?.showWatermark !== false,
    },
    meta: {
      date: formatDate(visitAt),
      time: formatTime(visitAt),
      protocol,
    },
    patient: {
      name: [firstName, lastName].filter(Boolean).join(" "),
      dateOfBirth: formatDate(patient?.dateOfBirth ?? patient?.DateOfBirth ?? caseData?.patientDateOfBirth ?? caseData?.PatientDateOfBirth).replaceAll(".", "/"),
      gender: genderRaw ? getGenderLabel(genderRaw) : "",
      phone: clean(patient?.phone ?? patient?.Phone ?? caseData?.patientPhone ?? caseData?.PatientPhone),
    },
    visit: {
      date: formatDate(visitAt),
      time: formatTime(visitAt),
      doctor: doctorLine(doctorInfo?.name || caseData?.assignedDoctorName || caseData?.AssignedDoctorName),
      service: clean(caseData?.serviceName ?? caseData?.ServiceName),
      protocol,
    },
    vitals: vitalRows,
    anamneza: clean(report?.anamneza ?? report?.Anamneza),
    ekzaminimi: clean(report?.ekzaminimi ?? report?.Ekzaminimi),
    diagnosis: clean(report?.diagnosis ?? report?.Diagnosis),
    therapy: clean(report?.therapy ?? report?.Therapy),
    labFileNames,
    signoff: {
      name: doctorLine(doctorInfo?.name || caseData?.assignedDoctorName || caseData?.AssignedDoctorName),
      signatureBase64: doctorInfo?.signatureBase64 || null,
      stampBase64: doctorInfo?.stampBase64 || null,
    },
  };
}

function contentWidth(ctx) {
  return ctx.pageWidth - PAGE_MARGIN * 2;
}

/**
 * Helvetica is a WinAnsi font. A character outside that set (subscript ₂,
 * and similar) makes jsPDF encode the whole line as UTF-16 while the font
 * stays single-byte, so the viewer paints null glyphs, a different face,
 * and a line about twice as wide as measured.
 */
function pdfText(doc, value) {
  const raw = String(value ?? "");
  if (!raw) return "";
  const extra = doc.internal.getFont()?.metadata?.Unicode?.encoding?.WinAnsiEncoding;
  let out = "";
  for (const ch of raw) {
    const code = ch.codePointAt(0);
    if (code === 10 || code === 13 || (code >= 32 && code <= 255)) {
      out += ch;
      continue;
    }
    if (extra && extra[code]) {
      out += ch;
      continue;
    }
    if (code >= 0x2080 && code <= 0x2089) {
      out += String(code - 0x2080);
      continue;
    }
    if (code === 0x2070) {
      out += "0";
      continue;
    }
    if (code >= 0x2074 && code <= 0x2079) {
      out += String(code - 0x2070);
      continue;
    }
    if (code === 0x200b || code === 0x200c || code === 0x200d || code === 0xfeff) continue;
    out += "?";
  }
  return out;
}

/**
 * Painted width in mm. jsPDF's getTextWidth subtracts kerning, but the PDF
 * draws the string without those gaps, so long lines spill past the box.
 */
function textWidth(doc, text) {
  const fontSize = doc.internal.getFontSize();
  return (
    (doc.getStringUnitWidth(pdfText(doc, text), { doKerning: false }) * fontSize) /
    doc.internal.scaleFactor
  );
}

/** Wrap on spaces so a sentence stays inside the box instead of being cut mid-word. */
function wrapLines(doc, text, maxWidth) {
  const width = Math.max(8, maxWidth);
  const limit = Math.max(8, width - 0.8);
  const paragraphs = pdfText(doc, text).replace(/\r\n/g, "\n").split("\n");
  const lines = [];
  const splitLongWord = (word) => {
    let rest = word;
    while (rest.length) {
      let low = 1;
      let high = rest.length;
      while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        if (textWidth(doc, rest.slice(0, mid)) <= limit) low = mid;
        else high = mid - 1;
      }
      const take = Math.max(1, low);
      lines.push(rest.slice(0, take));
      rest = rest.slice(take);
    }
  };
  paragraphs.forEach((paragraph) => {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines.push("");
      return;
    }
    let current = "";
    words.forEach((word) => {
      const next = current ? `${current} ${word}` : word;
      if (textWidth(doc, next) <= limit) {
        current = next;
        return;
      }
      if (current) lines.push(current);
      if (textWidth(doc, word) <= limit) current = word;
      else {
        splitLongWord(word);
        current = "";
      }
    });
    if (current) lines.push(current);
  });
  return lines.length ? lines : ["—"];
}

function footerTextY(pageHeight) {
  return pageHeight - FOOTER_TEXT_INSET;
}

function signoffTop(pageHeight) {
  return footerTextY(pageHeight) - 3.2 - SIGNOFF_FOOTER_GAP - SIGNOFF_HEIGHT;
}

function contentBottom(pageHeight) {
  return signoffTop(pageHeight) - 1.5;
}

function fitLine(doc, text, maxWidth) {
  const raw = pdfText(doc, clean(text) || "—");
  if (textWidth(doc, raw) <= maxWidth) return raw;
  let low = 1;
  let high = raw.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    const candidate = `${raw.slice(0, mid).trimEnd()}…`;
    if (textWidth(doc, candidate) <= maxWidth) low = mid;
    else high = mid - 1;
  }
  return `${raw.slice(0, Math.max(1, low)).trimEnd()}…`;
}

function drawWatermark(doc, ctx) {  
  if (!ctx.logo || ctx.watermarked.has(ctx.page)) return;
  const maxW = ctx.pageWidth * 0.75;
  const size = imageSize(doc, ctx.logo, maxW, ctx.pageHeight * 0.6);
  if (!size) return;
  ctx.watermarked.add(ctx.page);
  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: WATERMARK_OPACITY }));
  paintImage(
    doc,
    ctx.logo,
    (ctx.pageWidth - size.w) / 2,
    (ctx.pageHeight - size.h) / 2 - ctx.pageHeight * 0.09,
    size.w,
    size.h
  );
  doc.restoreGraphicsState();
}

function drawHeader(doc, ctx, data) {
  const top = ctx.y;
  const logoBox = 20;
  const logo = drawReportImage(doc, data.clinic.logoBase64, PAGE_MARGIN, top, logoBox, logoBox);
  const nameX = PAGE_MARGIN + (logo ? logo.w + 3 : 0);
  const nameMax = Math.max(50, contentWidth(ctx) - (logo ? logo.w + 6 : 0));
  const clinicName = data.clinic.name ? data.clinic.name.toUpperCase() : "";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  const nameLines = clinicName ? wrapLines(doc, clinicName, nameMax).slice(0, 2) : [];
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const addressLabelW = textWidth(doc, "Adresa: ");
  const phoneLabelW = textWidth(doc, "Tel: ");
  const addressLines = wrapLines(doc, data.clinic.address || "—", Math.max(20, nameMax - addressLabelW)).slice(0, 3);
  const phoneLines = wrapLines(doc, data.clinic.phone || "—", Math.max(20, nameMax - phoneLabelW)).slice(0, 2);
  const blockH = nameLines.length * 5.4 + addressLines.length * 3.6 + phoneLines.length * 3.6 + 1;
  const logoH = logo ? logo.h : 0;
  const textTop = top + Math.max(0, (Math.max(logoH, logoBox) - blockH) / 2);
  let textY = textTop + 4.6;
  if (nameLines.length) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...INK);
    doc.text(nameLines, nameX, textY);
    textY += nameLines.length * 5.4;
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...LABEL);
  addressLines.forEach((line, index) => {
    if (index === 0) {
      doc.setTextColor(...HINT);
      doc.text("Adresa:", nameX, textY);
    }
    doc.setTextColor(...LABEL);
    doc.text(line, nameX + addressLabelW, textY);
    textY += 3.6;
  });
  phoneLines.forEach((line, index) => {
    if (index === 0) {
      doc.setTextColor(...HINT);
      doc.text("Tel:", nameX, textY);
    }
    doc.setTextColor(...LABEL);
    doc.text(line, nameX + phoneLabelW, textY);
    textY += 3.6;
  });

  const headerBottom = Math.max(top + logoH, textY) + 1.2;
  doc.setDrawColor(214, 214, 214);
  doc.setLineWidth(0.3);
  doc.line(PAGE_MARGIN, headerBottom, ctx.pageWidth - PAGE_MARGIN, headerBottom);
  ctx.y = headerBottom + 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...INK);
  doc.text("RAPORT SPECIALISTIK", ctx.pageWidth / 2, ctx.y, { align: "center" });
  ctx.y += 4.2;
}

function drawSectionHeader(doc, ctx, title) {
  const x = PAGE_MARGIN;
  const w = contentWidth(ctx);
  const h = 6.2;
  doc.setFillColor(...HEADER_BG);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.rect(x, ctx.y, w, h, "FD");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  doc.text(title, x + 2.2, ctx.y + 4.3);
  ctx.y += h;
}

function drawInfoRow(doc, ctx, label, value) {
  const x = PAGE_MARGIN;
  const w = contentWidth(ctx);
  const padX = 2.4;
  const h = 8.4;
  if (ctx.y + h > contentBottom(ctx.pageHeight)) return;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(BODY_FONT);
  const labelW = textWidth(doc, label) + 3;
  const valueMax = Math.max(20, w - padX * 2 - labelW - 3);
  const valueLine = fitLine(doc, value, valueMax);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.rect(x, ctx.y, w, h);
  doc.setTextColor(...LABEL);
  doc.text(label, x + padX, ctx.y + 5.6);
  doc.setTextColor(...INK);
  const lineW = textWidth(doc, valueLine);
  doc.text(valueLine, x + w - padX - lineW, ctx.y + 5.6);
  ctx.y += h;
}

function beginSection(doc, ctx) {
  if (ctx.y + 8 > contentBottom(ctx.pageHeight)) return false;
  ctx.y += 1;
  return true;
}

function drawPatient(doc, ctx, data) {
  if (!beginSection(doc, ctx)) return;
  drawSectionHeader(doc, ctx, "Të dhënat e pacientit");
  drawInfoRow(doc, ctx, "Emri i pacientit", data.patient.name);
  drawInfoRow(doc, ctx, "Gjinia", data.patient.gender);
  drawInfoRow(doc, ctx, "Data e lindjes", data.patient.dateOfBirth);
  if (data.patient.phone) drawInfoRow(doc, ctx, "Telefoni", data.patient.phone);
  if (data.meta.protocol) drawInfoRow(doc, ctx, "Nr. protokollit", data.meta.protocol);
}

function drawVitals(doc, ctx, rows) {
  if (!rows.length) return;
  if (!beginSection(doc, ctx)) return;
  drawSectionHeader(doc, ctx, "Shenjat vitale");
  const x = PAGE_MARGIN;
  const w = contentWidth(ctx);
  const h = 8.4;
  if (ctx.y + h > contentBottom(ctx.pageHeight)) return;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(BODY_FONT);
  const parts = rows.map(([label, value]) => `${label}: ${value}`);
  const line = fitLine(doc, parts.join("   "), w - 5);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.rect(x, ctx.y, w, h);
  doc.setTextColor(...INK);
  doc.text(line, x + 2.4, ctx.y + 5.6);
  ctx.y += h;
}

function drawClinical(doc, ctx, data) {
  const fields = [
    ["Anamneza", data.anamneza || "—"],
    ["Ekzaminimi", data.ekzaminimi || "—"],
    ["Diagnoza", data.diagnosis || "—"],
    ["Terapia / Rekomandimi", data.therapy || "—"],
  ];
  if (data.labFileNames.length) {
    fields.push(["Laboratori", data.labFileNames.map((name) => `- ${name}`).join("\n")]);
  }
  if (!beginSection(doc, ctx)) return;
  const textX = 2.6;
  const rightPad = 3;
  const textW = Math.max(24, contentWidth(ctx) - textX - rightPad);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(BODY_FONT);
  const rows = [];
  fields.forEach(([label, value], fieldIndex) => {
    rows.push({ kind: "label", text: label, h: 5.4 });
    wrapLines(doc, value || "—", textW).forEach((line) => {
      rows.push({ kind: "text", text: fitLine(doc, line, textW), h: 5.8 });
    });
    rows.push({ kind: "spacer", h: 1.8 });
    if (fieldIndex < fields.length - 1) rows.push({ kind: "divider", h: 3.2 });
  });

  drawSectionHeader(doc, ctx, "Shënime klinike");
  const padTop = 3.4;
  const padBottom = 4;
  const available = contentBottom(ctx.pageHeight) - ctx.y;
  if (available < padTop + 7) return;
  let used = padTop;
  let index = 0;
  while (index < rows.length && used + rows[index].h + padBottom <= available) {
    used += rows[index].h;
    index += 1;
  }
  const truncated = index < rows.length;
  if (truncated && index > 0) {
    const last = rows[index - 1];
    if (last.kind === "text") last.text = fitLine(doc, `${String(last.text).replace(/…$/, "")}…`, textW);
  }
  const boxH = available;
  const x = PAGE_MARGIN;
  const w = contentWidth(ctx);
  const boxBottom = ctx.y + boxH;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.rect(x, ctx.y, w, boxH);
  let y = ctx.y + padTop;
  for (let i = 0; i < index; i += 1) {
    const row = rows[i];
    const baseline = y + Math.min(row.h - 1.4, 4);
    if (baseline > boxBottom - 3) break;
    if (row.kind === "label") {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(BODY_FONT);
      doc.setTextColor(0, 0, 0);
      doc.text(row.text, x + textX, baseline);
    } else if (row.kind === "text") {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(BODY_FONT);
      doc.setTextColor(...INK);
      doc.text(fitLine(doc, row.text, textW), x + textX, baseline);
    } else if (row.kind === "divider") {
      const hasMore = rows.slice(i + 1, index).some((next) => next.kind === "label");
      if (hasMore) {
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.25);
        doc.line(x, y + row.h / 2, x + w, y + row.h / 2);
      }
    }
    y += row.h;
  }
  ctx.y += boxH;
}

function drawSignoff(doc, ctx, data) {
  const gap = 8;
  const colW = (contentWidth(ctx) - gap) / 2;
  const rightX = PAGE_MARGIN + colW + gap;
  const top = signoffTop(ctx.pageHeight);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text("Mjeku përgjegjës", PAGE_MARGIN, top + 3.2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  const name = data.signoff.name ? fitLine(doc, data.signoff.name, colW - 4) : "—";
  doc.text(name, PAGE_MARGIN, top + 8);
  drawReportImage(doc, data.signoff.signatureBase64, PAGE_MARGIN, top + 9.5, 46, 10);
  drawReportImage(doc, data.signoff.stampBase64, rightX, top + 3, 22, 16);
  const lineY = top + SIGNOFF_HEIGHT - 4.5;
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.3);
  doc.line(PAGE_MARGIN, lineY, PAGE_MARGIN + 62, lineY);
  doc.line(rightX, lineY, rightX + 62, lineY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text("Nënshkrimi", PAGE_MARGIN, lineY + 3.4);
  doc.text("Vula", rightX, lineY + 3.4);
}

function drawDocumentFooter(doc, ctx, data) {
  const pageCount = doc.getNumberOfPages();
  const clinicName = data.clinic.name ? data.clinic.name.toUpperCase() : "";
  const left = [clinicName, "Dokument zyrtar mjekësor"];
  if (data.meta.protocol) left.push(`Nr. protokollit: ${data.meta.protocol}`);
  const leftText = left.filter(Boolean).join(" · ");
  const rightText = [data.clinic.address, data.clinic.phone ? `Tel: ${data.clinic.phone}` : ""]
    .filter(Boolean)
    .join(" · ");

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    const yText = footerTextY(ctx.pageHeight);
    const yLine = yText - 3.2;
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.25);
    doc.line(PAGE_MARGIN, yLine, ctx.pageWidth - PAGE_MARGIN, yLine);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...LABEL);
    const rightW = rightText ? textWidth(doc, rightText) : 0;
    const gap = rightText ? 4 : 0;
    const leftMax = Math.max(contentWidth(ctx) - rightW - gap, contentWidth(ctx) * 0.45);
    const leftLines = wrapLines(doc, leftText, leftMax);
    const leftLine = leftLines[0] || "";
    doc.text(leftLine, PAGE_MARGIN, yText);
    if (rightText) doc.text(rightText, ctx.pageWidth - PAGE_MARGIN - rightW, yText);
  }
  doc.setPage(pageCount);
}

/**
 * Build the visit PDF in memory. Callers can download it or return the blob.
 */
export function createCaseReportPdfDocument(caseData, clinicHeader = null, doctorInfo = null) {
  const data = getReportData(caseData, clinicHeader || {}, doctorInfo || {});
  const doc = new jsPDF({ format: "a4", unit: "mm" });
  const ctx = {
    pageWidth: doc.internal.pageSize.getWidth(),
    pageHeight: doc.internal.pageSize.getHeight(),
    y: PAGE_TOP,
    page: 1,
    logo: data.clinic.showWatermark ? data.clinic.logoBase64 : null,
    watermarked: new Set(),
  };

  drawWatermark(doc, ctx);
  drawHeader(doc, ctx, data);
  drawPatient(doc, ctx, data);
  drawVitals(doc, ctx, data.vitals);
  drawClinical(doc, ctx, data);
  drawSignoff(doc, ctx, data);
  drawDocumentFooter(doc, ctx, data);
  while (doc.getNumberOfPages() > 1) doc.deletePage(doc.getNumberOfPages());

  const safeName = (data.patient.name || "raport").replace(/[^\p{L}\p{N}]+/gu, "_").replace(/^_+|_+$/g, "") || "raport";
  const filename = `raport_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`;
  return { doc, filename };
}


/**
 * Generate and download a PDF report using jsPDF only (no HTML).
 */
export function downloadCaseReportPdf(caseData, clinicHeader = null, doctorInfo = null) {
  const { doc, filename } = createCaseReportPdfDocument(caseData, clinicHeader, doctorInfo);
  doc.save(filename);
}

/**
 * Download case report PDF with clinic header from API (client-generated fallback).
 */
export async function downloadCaseReportPdfWithClinicHeader(caseData, doctorInfo = null) {
  const { getClinicProfile, getLogoAsBase64 } = await import("../api/clinic");
  let clinicHeader = {};
  try {
    const profile = await getClinicProfile();
    const name = profile?.name ?? profile?.Name ?? "";
    const address = profile?.address ?? profile?.Address ?? "";
    const phone = profile?.phone ?? profile?.Phone ?? "";
    const email = profile?.email ?? profile?.Email ?? "";
    const logoUrl = profile?.logoUrl ?? profile?.LogoUrl;
    const rawLogo = logoUrl ? await getLogoAsBase64(logoUrl) : null;
    const themeId =
      profile?.colorThemePreferences?.themeId ??
      profile?.colorThemePreferences?.ThemeId ??
      profile?.ColorThemePreferences?.themeId ??
      "";
    clinicHeader = {
      name,
      address,
      phone,
      email,
      themeId,
      showWatermark: profile?.showPdfWatermark !== false,
      useProtocolNumber: profile?.protocolPreferences?.useProtocolNumber === true,
      logoBase64: (await normalizeReportImage(rawLogo)) || rawLogo || null,
    };
  } catch (_) {}
  const preparedDoctor = doctorInfo
    ? {
        ...doctorInfo,
        signatureBase64: (await normalizeReportImage(doctorInfo.signatureBase64)) || doctorInfo.signatureBase64 || null,
        stampBase64: (await normalizeReportImage(doctorInfo.stampBase64)) || doctorInfo.stampBase64 || null,
      }
    : null;
  downloadCaseReportPdf(caseData, clinicHeader, preparedDoctor);
}

/**
 * Download case report PDF from backend (GET /api/PatientCase/{id}/pdf).
 * Uses filename from Content-Disposition when present.
 * @param {string} caseId - case id (GUID)
 * @throws On 404 (case not found / not in your clinic) or network errors
 */
export async function downloadCaseReportPdfFromBackend(caseId) {
  const { blob, filename } = await getCaseReportPdf(caseId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Open the backend case report PDF in a hidden frame and trigger the browser print dialog.
 * Resolves as soon as print() is called so the UI can print again (afterprint is unreliable in iframes).
 * @param {string} caseId - case id (GUID)
 */
export async function printCaseReportPdfFromBackend(caseId) {
  const { blob } = await getCaseReportPdf(caseId);
  const url = URL.createObjectURL(blob);

  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "Raport mjekësor");
    iframe.style.cssText =
      "position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none;";

    let settled = false;
    let printed = false;

    const cleanup = () => {
      try {
        if (iframe.parentNode) document.body.removeChild(iframe);
      } catch (_) {}
      URL.revokeObjectURL(url);
    };

    const settleOk = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const settleErr = (err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    };

    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      try {
        const win = iframe.contentWindow;
        if (!win?.print) {
          settleErr(new Error("Printimi nuk mbështetet në këtë shfletues."));
          return;
        }
        const onAfterPrint = () => cleanup();
        win.addEventListener("afterprint", onAfterPrint, { once: true });
        window.addEventListener("afterprint", onAfterPrint, { once: true });
        win.focus();
        win.print();
        settleOk();
        window.setTimeout(cleanup, 90_000);
      } catch (e) {
        settleErr(e);
      }
    };

    iframe.onload = triggerPrint;
    iframe.onerror = () => settleErr(new Error("Dështoi ngarkimi i PDF për printim."));

    iframe.src = url;
    document.body.appendChild(iframe);

    window.setTimeout(() => {
      if (!printed) triggerPrint();
    }, 1500);
  });
}
